import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowLeft, Car, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "mechanic" ? "mechanic" : "client";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: initialRole,
    skills: "",
    // Identity verification (admin-visible only)
    idMode: "id", // "id" | "passport" — client only chooses one
    idNumber: "",
    passportNumber: "",
    driverLicenceNumber: "",
    tradeQualificationNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validatePhone = (raw) => {
    const digits = (raw || "").replace(/[^\d+]/g, "");
    let rest = "";
    if (digits.startsWith("+27")) rest = digits.slice(3);
    else if (digits.startsWith("27")) rest = digits.slice(2);
    else if (digits.startsWith("0")) rest = digits.slice(1);
    else return null;
    if (rest.length !== 9 || !/^\d+$/.test(rest)) return null;
    return `+27${rest}`;
  };

  // Identity verification format checks — mirror backend rules
  const normSpaces = (v) => (v || "").replace(/\s+/g, "");
  const checkIdNumber = (v) => /^\d{13}$/.test(normSpaces(v));
  const checkPassport = (v) => /^[A-Za-z0-9]{6,12}$/.test(normSpaces(v));
  const checkDriverLicence = (v) => /^[A-Za-z0-9]{5,15}$/.test(normSpaces(v));
  const checkTradeQual = (v) => /^[A-Za-z0-9 _.\-/]{3,40}$/.test((v || "").trim());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const normalizedPhone = validatePhone(form.phone);
    if (!normalizedPhone) {
      setError("Invalid phone number. Use South African format: +27 82 555 0100 or 082 555 0100.");
      return;
    }

    // Verification validation — role-specific
    if (form.role === "client") {
      if (form.idMode === "id") {
        if (!checkIdNumber(form.idNumber)) {
          setError("Enter a valid 13-digit South African ID number.");
          return;
        }
      } else if (!checkPassport(form.passportNumber)) {
        setError("Enter a valid passport number (6–12 letters/digits).");
        return;
      }
      if (form.driverLicenceNumber && !checkDriverLicence(form.driverLicenceNumber)) {
        setError("Driver's licence must be 5–15 letters/digits.");
        return;
      }
    } else {
      // mechanic
      if (!checkIdNumber(form.idNumber)) {
        setError("Mechanics must provide a valid 13-digit South African ID number.");
        return;
      }
      if (!checkDriverLicence(form.driverLicenceNumber)) {
        setError("Mechanics must provide a valid driver's licence (5–15 letters/digits).");
        return;
      }
      if (form.tradeQualificationNumber && !checkTradeQual(form.tradeQualificationNumber)) {
        setError("Trade qualification must be 3–40 characters.");
        return;
      }
    }

    setLoading(true);
    const payload = {
      name: form.name,
      email: form.email,
      phone: normalizedPhone,
      password: form.password,
      role: form.role,
      skills: form.role === "mechanic"
        ? form.skills.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      // Identity verification — backend only stores what's provided
      driverLicenceNumber: form.driverLicenceNumber ? normSpaces(form.driverLicenceNumber) : undefined,
    };
    if (form.role === "client") {
      if (form.idMode === "id") payload.idNumber = normSpaces(form.idNumber);
      else payload.passportNumber = normSpaces(form.passportNumber);
    } else {
      payload.idNumber = normSpaces(form.idNumber);
      if (form.tradeQualificationNumber) {
        payload.tradeQualificationNumber = form.tradeQualificationNumber.trim();
      }
    }
    // Strip undefined keys so the request stays clean
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    console.log("[Register] POST /api/auth/register →", { ...payload, password: "***" });
    try {
      await register(payload);
      console.log("[Register] success");
      toast.success("Account created");
      navigate("/app");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      console.error("[Register] FAILED →", {
        status: e.response?.status,
        detail: e.response?.data?.detail,
        message: e.message,
      });
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell">
      <div className="px-6 pt-6 pb-4 flex items-center gap-3">
        <Link to="/" data-testid="register-back-btn"><ArrowLeft /></Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          Create Account
        </span>
      </div>

      <div className="px-6 pb-4">
        <h1 className="font-display text-4xl font-black leading-none tracking-tight">
          Join AutoFix<br />Rescue.
        </h1>
        <p className="text-[#9CA3AF] mt-2 text-sm">
          Get help — or help others. Pick your role to begin.
        </p>
      </div>

      <form onSubmit={submit} className="px-6 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <RoleCard
            active={form.role === "client"}
            onClick={() => setForm({ ...form, role: "client" })}
            icon={<Car size={22} />}
            label="I'm a Driver"
            sub="Need help"
            testid="role-client-btn"
          />
          <RoleCard
            active={form.role === "mechanic"}
            onClick={() => {
              const seen = (() => { try { return localStorage.getItem("afr_mechanic_intro_seen") === "1"; } catch { return true; } })();
              if (!seen) {
                navigate("/mechanic-intro");
                return;
              }
              setForm({ ...form, role: "mechanic" });
            }}
            icon={<Wrench size={22} />}
            label="I'm a Mechanic"
            sub="Offer help"
            testid="role-mechanic-btn"
          />
        </div>

        <Field label="Full Name">
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
            placeholder="Thabo Mokoena"
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
            placeholder="you@example.com"
          />
        </Field>

        <Field label="Phone">
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="register-phone-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
            placeholder="+27 ..."
          />
        </Field>

        <Field label="Password">
          <input
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
            placeholder="At least 6 characters"
          />
        </Field>

        {form.role === "mechanic" && (
          <Field label="Skills (comma separated)">
            <input
              value={form.skills}
              onChange={(e) => setForm({ ...form, skills: e.target.value })}
              data-testid="register-skills-input"
              className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
              placeholder="battery, towing, diagnostics"
            />
          </Field>
        )}

        {/* ===== Identity verification ===== */}
        <div className="pt-2" data-testid="register-verification-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3CFF7E] font-display mb-2">
            Identity verification
          </div>
          <p className="text-[11px] text-[#9CA3AF] -mt-1 mb-3 leading-relaxed">
            Stored securely. Only AutoFix Rescue admins can view these details — never other drivers or mechanics.
          </p>

          {form.role === "client" ? (
            <>
              <div className="grid grid-cols-2 gap-2 mb-3" role="tablist" data-testid="register-idmode-tabs">
                <IdModeTab
                  active={form.idMode === "id"}
                  onClick={() => setForm({ ...form, idMode: "id" })}
                  label="SA ID"
                  testid="register-idmode-id"
                />
                <IdModeTab
                  active={form.idMode === "passport"}
                  onClick={() => setForm({ ...form, idMode: "passport" })}
                  label="Passport"
                  testid="register-idmode-passport"
                />
              </div>
              {form.idMode === "id" ? (
                <Field label="SA ID number (13 digits)">
                  <input
                    required
                    inputMode="numeric"
                    value={form.idNumber}
                    onChange={(e) => setForm({ ...form, idNumber: e.target.value })}
                    data-testid="register-id-input"
                    className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E] tracking-wider"
                    placeholder="9001015009087"
                    maxLength={13}
                  />
                </Field>
              ) : (
                <Field label="Passport number">
                  <input
                    required
                    value={form.passportNumber}
                    onChange={(e) => setForm({ ...form, passportNumber: e.target.value.toUpperCase() })}
                    data-testid="register-passport-input"
                    className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E] tracking-wider"
                    placeholder="A12345678"
                    maxLength={12}
                  />
                </Field>
              )}
              <div className="mt-3">
                <Field label="Driver's licence (optional)">
                  <input
                    value={form.driverLicenceNumber}
                    onChange={(e) => setForm({ ...form, driverLicenceNumber: e.target.value.toUpperCase() })}
                    data-testid="register-licence-input"
                    className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E] tracking-wider"
                    placeholder="Optional"
                    maxLength={15}
                  />
                </Field>
              </div>
            </>
          ) : (
            <>
              <Field label="SA ID number (13 digits)">
                <input
                  required
                  inputMode="numeric"
                  value={form.idNumber}
                  onChange={(e) => setForm({ ...form, idNumber: e.target.value })}
                  data-testid="register-id-input"
                  className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E] tracking-wider"
                  placeholder="9001015009087"
                  maxLength={13}
                />
              </Field>
              <div className="mt-3">
                <Field label="Driver's licence (required)">
                  <input
                    required
                    value={form.driverLicenceNumber}
                    onChange={(e) => setForm({ ...form, driverLicenceNumber: e.target.value.toUpperCase() })}
                    data-testid="register-licence-input"
                    className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E] tracking-wider"
                    placeholder="Code 08 / 10 / 14 etc."
                    maxLength={15}
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Trade qualification / licence (optional)">
                  <input
                    value={form.tradeQualificationNumber}
                    onChange={(e) => setForm({ ...form, tradeQualificationNumber: e.target.value })}
                    data-testid="register-trade-input"
                    className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
                    placeholder="e.g. RMI-2024-1234"
                    maxLength={40}
                  />
                </Field>
              </div>
            </>
          )}
        </div>

        {error && (
          <div className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3" data-testid="register-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? "Creating..." : "Create Account"}
        </button>

        <p className="text-sm text-[#9CA3AF] text-center pb-6">
          Have an account?{" "}
          <Link to="/login" className="font-bold text-[#3CFF7E]">Sign in</Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
        {label}
      </label>
      {children}
    </div>
  );
}

function RoleCard({ active, onClick, icon, label, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`border rounded-md p-4 text-left transition-all ${
        active
          ? "border-[#3CFF7E] bg-[#3CFF7E] text-[#0A0A0A]"
          : "border-white/20 bg-[#111827] text-white hover:border-white"
      }`}
    >
      <div className="mb-2">{icon}</div>
      <div className="font-display font-bold text-sm">{label}</div>
      <div className={`text-[11px] ${active ? "text-white/80" : "text-[#9CA3AF]"}`}>{sub}</div>
    </button>
  );
}

function IdModeTab({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`h-10 rounded-md text-[11px] font-display font-bold uppercase tracking-wider transition-all active:scale-[0.98] ${
        active
          ? "bg-[#3CFF7E] text-[#0A0A0A]"
          : "border border-white/15 bg-[#111827] text-[#9CA3AF]"
      }`}
    >
      {label}
    </button>
  );
}
